import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router';

import { Button, Drawer, Flex, Layout } from 'antd';

import AiIcon from '../../../../../components/AiIcon';
import { HeaderEmail } from '../../../../../components/HeaderEmail';
import LangSwitcher from '../../../../../components/LangSwitcher';
import Logout from '../../../../../components/Logout';

import { MY_FILES_PATH } from '../../../../../constants/routes';
import DisplaySizeContext from '../../../../../context/DisplaySize';
import { useHomeMessageId } from '../../../hooks/useHomeMessageId';
import { useHomeAuthModal } from '../HomeAuthModalContext';

import { ReduxState } from '../../../../../types/app';

const { Header } = Layout;

const navigationItems = [
  { key: 'nav.integration', href: '#integration' },
  { key: 'nav.training', href: '#training' },
  { key: 'nav.security', href: '#security' },
  { key: 'nav.contacts', href: '#contacts' },
];

const SCROLL_OFFSET_PX = 10;

const scrollToHashTarget = (hash: string) => {
  const id = hash.startsWith('#') ? hash.slice(1) : hash;

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const element = document.getElementById(id);
      if (!element) return;

      const top =
        element.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET_PX;

      window.scrollTo({ top, behavior: 'smooth' });
    });
  });
};

const HeaderNav = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const displaySize = useContext(DisplaySizeContext);
  const { openLogin, openRegister } = useHomeAuthModal();
  const homeMessageId = useHomeMessageId();
  const isLoggedIn = useSelector(
    (state: ReduxState) => state.Client.isLoggedIn
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isDesktop = displaySize === 'desktop' || displaySize === 'desktopLg';

  useEffect(() => {
    if (isDesktop) {
      setIsDrawerOpen(false);
    }
  }, [isDesktop]);

  const handleInPageNavClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string,
    closeDrawer = false
  ) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    navigate({
      pathname: location.pathname,
      search: location.search,
      hash: href,
    });
    scrollToHashTarget(href);

    if (closeDrawer) {
      setIsDrawerOpen(false);
    }
  };

  const renderNavItems = (variant: 'desktop' | 'drawer') =>
    navigationItems.map(({ href, key }) => {
      const isDrawer = variant === 'drawer';

      return (
        <Link
          key={key}
          className={isDrawer ? 'home-page__drawer-nav-link' : undefined}
          to={{
            pathname: location.pathname,
            search: location.search,
            hash: href,
          }}
          onClick={(event) => handleInPageNavClick(event, href, isDrawer)}
        >
          <FormattedMessage id={homeMessageId(key)} />
        </Link>
      );
    });

  const renderActions = () => (
    <>
      <div className="home-page__language">
        <LangSwitcher />
      </div>
      {isLoggedIn ? (
        <>
          <HeaderEmail to={MY_FILES_PATH} />
          <Logout />
        </>
      ) : (
        <>
          <Button className="btn-gradient" onClick={openRegister}>
            <FormattedMessage id={homeMessageId('header.signup')} />
          </Button>
          <Button className="btn-gradient" onClick={openLogin}>
            <FormattedMessage id={homeMessageId('header.login')} />
          </Button>
        </>
      )}
    </>
  );

  const renderDrawerHeader = () => (
    <div className="header__drawer-header">
      {isLoggedIn ? (
        <>
          <Flex
            className="header__drawer-header-row"
            align="center"
            justify="space-between"
            gap={16}
          >
            <HeaderEmail
              className="header__drawer-email"
              to={MY_FILES_PATH}
              onClick={() => setIsDrawerOpen(false)}
            />

            <Logout />
          </Flex>

          <Flex className="header__drawer-header-row" align="center">
            <div className="home-page__language home-page__language--drawer-full">
              <LangSwitcher />
            </div>
          </Flex>
        </>
      ) : (
        <Flex
          className="header__drawer-header-row"
          align="center"
          justify="space-between"
          gap={16}
        >
          <div className="home-page__language home-page__language--drawer-grow">
            <LangSwitcher />
          </div>

          <Button
            className="btn-gradient"
            onClick={() => {
              openLogin();
              setIsDrawerOpen(false);
            }}
          >
            <FormattedMessage id={homeMessageId('header.login')} />
          </Button>
        </Flex>
      )}
    </div>
  );

  return (
    <Header className="home-page__header">
      <Link className="home-page__brand" to="/">
        <AiIcon name="header-logo-img" />
        <span className="home-page__brand-logo-text">
          <span className="home-page__brand-logo-text-primary">
            <FormattedMessage id={homeMessageId('brand.primary')} />
          </span>{' '}
          <span className="home-page__brand-logo-text-secondary">
            <FormattedMessage id={homeMessageId('brand.secondary')} />
          </span>
        </span>
      </Link>

      {isDesktop ? (
        <>
          <nav
            className="home-page__nav"
            aria-label={intl.formatMessage({
              id: homeMessageId('nav.ariaLabel'),
            })}
          >
            {renderNavItems('desktop')}
          </nav>

          <Flex className="home-page__actions" align="center">
            {renderActions()}
          </Flex>
        </>
      ) : (
        <>
          <button
            type="button"
            className="header__burger"
            aria-label={intl.formatMessage({
              id: homeMessageId('nav.ariaLabel'),
            })}
            aria-expanded={isDrawerOpen}
            onClick={() => setIsDrawerOpen(true)}
          >
            <AiIcon name="burgerMenu" />
          </button>

          <Drawer
            className="home-page__drawer"
            placement="left"
            open={isDrawerOpen}
            closable={false}
            onClose={() => setIsDrawerOpen(false)}
            title={renderDrawerHeader()}
          >
            <nav
              className="home-page__drawer-nav"
              aria-label={intl.formatMessage({
                id: homeMessageId('nav.ariaLabel'),
              })}
            >
              {renderNavItems('drawer')}
            </nav>
          </Drawer>
        </>
      )}
    </Header>
  );
};

export default HeaderNav;
